/*
  r3d-gpx.js — GPXをブラウザ内で解析する。サーバーは使わない。

  製造側の確定仕様に合わせている点（変更する前に理由を確認すること）:

  - 累積標高はGPXが持つ標高値の正の差分の合計。DEMから測り直さない。
    累積標高は「どれだけ細かい刻みで測るか」で値が変わる量なので、
    測り直すと顧客がヤマレコ/Strava で見ている数字とずれる。
  - 日付は必ずJSTへ変換してから取る。GPXの <time> はUTC固定なので、
    未明発の山行では変換しないとUTC日付が前日になる。
  - 距離は水平距離（2D）。標高を含めた3D距離は各アプリの表示と合わない。
  - 標高や時刻を持たないGPXでは、その項目を出さない（null を返す）。
*/

(function () {
  'use strict';

  const EARTH_R = 6371008.8; // m（WGS84の平均半径）

  function haversine(a, b) {
    const dLat = ((b.lat - a.lat) * Math.PI) / 180;
    const dLon = ((b.lon - a.lon) * Math.PI) / 180;
    const lat1 = (a.lat * Math.PI) / 180;
    const lat2 = (b.lat * Math.PI) / 180;
    const h =
      Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
    return 2 * EARTH_R * Math.asin(Math.min(1, Math.sqrt(h)));
  }

  /** GPXテキストを点列とトラック区切りに分解する */
  function parsePoints(xmlText) {
    const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
    if (doc.querySelector('parsererror')) {
      throw new Error('GPSログとして読めませんでした。ファイルが壊れている可能性があります。');
    }

    const points = [];
    /*
      segments は <trkseg> 単位。地図に線を引く単位で、記録の一時停止や
      GPSの途切れで切れる。ここで切らずに引くと、途切れた区間を直線が
      またいでしまう。

      色を分ける単位は <trk>。<trkseg> の切れ目は技術的な事故で入るもので
      山行の意味とは一致しないうえ、<trkseg> には名前を付ける場所がない。
      <trk> は <name> を持つ（ヤマレコなら「仙人ヶ岳」「赤雪山」「帰路」）。
      segTrack が各セグメントの親 <trk> の番号で、この2つを分けて持つことで
      「線は trkseg 単位で引き、色は trk 単位で決める」ができる。
    */
    const segments = []; // 各セグメントの [開始index, 終了index]
    const segTrack = []; // segments と同じ長さ。各セグメントの親 <trk> 番号
    const rawNames = []; // <trk> ごとの名前（点を持たない <trk> も含む）
    let trackName = '';

    const trks = Array.from(doc.getElementsByTagName('trk'));
    if (trks.length) {
      const firstName = trks[0].getElementsByTagName('name')[0];
      if (firstName) trackName = (firstName.textContent || '').trim();

      trks.forEach(function (trk, ti) {
        const nm = trk.getElementsByTagName('name')[0];
        rawNames[ti] = nm ? (nm.textContent || '').trim() : '';
        Array.from(trk.getElementsByTagName('trkseg')).forEach(function (seg) {
          const start = points.length;
          Array.from(seg.getElementsByTagName('trkpt')).forEach(function (pt) {
            const p = readPoint(pt);
            if (p) points.push(p);
          });
          if (points.length > start) {
            segments.push([start, points.length - 1]);
            segTrack.push(ti);
          }
        });
      });
    }

    // trk が無いGPX（ルートのみ / ウェイポイントのみ）へのフォールバック
    if (!points.length) {
      ['rtept', 'wpt'].forEach(function (tag) {
        if (points.length) return;
        const start = points.length;
        Array.from(doc.getElementsByTagName(tag)).forEach(function (pt) {
          const p = readPoint(pt);
          if (p) points.push(p);
        });
        if (points.length > start) {
          segments.push([start, points.length - 1]);
          segTrack.push(0);
          if (!rawNames.length) rawNames.push('');
        }
      });
    }

    /* 点を持たない <trk> を飛ばして 0..n-1 に詰め直す */
    const used = [];
    segTrack.forEach(function (ti) {
      if (used.indexOf(ti) === -1) used.push(ti);
    });
    used.sort(function (a, b) {
      return a - b;
    });
    const remap = {};
    used.forEach(function (ti, i) {
      remap[ti] = i;
    });
    const trackNames = used.map(function (ti) {
      return rawNames[ti] || '';
    });

    return {
      points: points,
      segments: segments,
      segTrack: segTrack.map(function (ti) {
        return remap[ti];
      }),
      trackNames: trackNames,
      trackName: trackName,
    };
  }

  /*
    標高として受け入れる範囲[m]。範囲外は「標高なし」として捨てる。

    GPXの <ele> は端末やアプリの不具合で桁が飛ぶことがあり、1e300 のような値も
    そのまま入ってくる。累積標高はGPXの値をそのまま刻む仕様なので、
    おかしな値を通すと刻印の数字がおかしくなる。
    日本の最低点（-4m前後の干拓地）と最高点（3,776m）に対して十分な幅を取り、
    海外の高峰（8,849m）も収まるようにしてある。
  */
  var ELE_MIN = -500;
  var ELE_MAX = 9000;

  function readPoint(el) {
    const lat = parseFloat(el.getAttribute('lat'));
    const lon = parseFloat(el.getAttribute('lon'));
    if (!isFinite(lat) || !isFinite(lon)) return null;

    const eleEl = el.getElementsByTagName('ele')[0];
    const timeEl = el.getElementsByTagName('time')[0];
    const ele = eleEl ? parseFloat(eleEl.textContent) : NaN;
    const timeText = timeEl ? (timeEl.textContent || '').trim() : '';

    return {
      lat: lat,
      lon: lon,
      ele: isFinite(ele) && ele >= ELE_MIN && ele <= ELE_MAX ? ele : null,
      time: timeText || null,
    };
  }

  /** JSTの年月日を YYYY/MM/DD で返す */
  const jstDate = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  /*
    JSTの日時を YYYY/MM/DD HH:MM で返す。
    hourCycle: 'h23' を明示するのは、hour12:false だけだと環境によって
    深夜0時が「24:00」になり、日付と1日ずれて見えるため。
  */
  const jstDateTime = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });

  function toJstDateTimeString(iso) {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return jstDateTime.format(d).replace(/-/g, '/');
  }

  function toJstDateString(iso) {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    // ja-JP + 2-digit は "2026/08/02" を返す
    return jstDate.format(d).replace(/-/g, '/');
  }

  /**
   * 点列から諸元を出す。
   * 返り値: { pointCount, distanceM, elevationGainM|null, dateLabel|null,
   *           bbox, segments, trackName }
   */
  function summarize(parsed) {
    const pts = parsed.points;
    if (pts.length < 2) {
      throw new Error('軌跡の点が足りません。トラックログを含むGPSログをお送りください。');
    }

    let distance = 0;
    for (let i = 1; i < pts.length; i++) {
      distance += haversine(pts[i - 1], pts[i]);
    }

    // 累積標高: GPXが持つ標高値の正の差分の合計（測り直さない）
    let gain = null;
    const withEle = pts.filter(function (p) {
      return p.ele !== null;
    });
    if (withEle.length >= 2) {
      gain = 0;
      for (let i = 1; i < withEle.length; i++) {
        const d = withEle[i].ele - withEle[i - 1].ele;
        if (d > 0) gain += d;
      }
    }

    // 日付: UTCで記録された時刻をJSTへ変換してから取る
    let dateLabel = null;
    const times = pts
      .map(function (p) {
        return p.time;
      })
      .filter(Boolean);
    /*
      startLabel / endLabel は画面表示用（時刻まで）。
      dateLabel は刻印用で日付のみのまま。刻印は「登った日」を刻むもので、
      分単位の時刻を彫る前提ではないため、ここは分けてある。

      ⚠️⚠️ **dateLabel の範囲表記は "YYYY/MM/DD - YYYY/MM/DD" のまま。年を落とさないこと。**
      **刻印に載る文字列（`r3d-designer.js` の `syncDateProp`）とは別物で、規則が違う。**
      あちらは 2026-08-29 に帰着日の年を落として "YYYY/MM/DD - MM/DD" にしたが、
      **こちらは `fillDatesFromGpx` が ' - ' で割って date 入力（ISO必須）へ戻すので、
      年を落とすと帰着日が入らなくなる。**
      **見た目が似ているので「揃える」と壊れる。** 用途が違う2つとして扱うこと。
    */
    let startLabel = null;
    let endLabel = null;
    if (times.length) {
      const first = toJstDateString(times[0]);
      const last = toJstDateString(times[times.length - 1]);
      if (first) dateLabel = first === last || !last ? first : first + ' - ' + last;
      startLabel = toJstDateTimeString(times[0]);
      endLabel = toJstDateTimeString(times[times.length - 1]);
    }

    const lats = pts.map(function (p) {
      return p.lat;
    });
    const lons = pts.map(function (p) {
      return p.lon;
    });

    let eleMin = null;
    let eleMax = null;
    if (withEle.length) {
      const eles = withEle.map(function (p) {
        return p.ele;
      });
      eleMin = Math.min.apply(null, eles);
      eleMax = Math.max.apply(null, eles);
    }

    /*
      日付ごとの区切り。JSTの日付が変わるところで切る。

      トラック区切り（trkseg）は記録の一時停止で入るもので、山行の意味とは
      一致しない。日別のほうが「1日目・2日目」という見方に素直に対応する。
      1点でも <time> を持たない点があるGPXでは日付で分けられないので空を返す
      （画面側はそれを見て、日付で分ける選択肢を出さない）。
    */
    const dateSegments = [];
    const dateLabels = [];
    const allTimed = pts.every(function (p) {
      return !!p.time;
    });
    if (allTimed && pts.length) {
      let start = 0;
      let cur = toJstDateString(pts[0].time);
      for (let i = 1; i < pts.length; i++) {
        const d = toJstDateString(pts[i].time);
        if (d !== cur) {
          dateSegments.push([start, i - 1]);
          dateLabels.push(cur);
          start = i;
          cur = d;
        }
      }
      dateSegments.push([start, pts.length - 1]);
      dateLabels.push(cur);
    }

    const bbox = {
      south: Math.min.apply(null, lats),
      north: Math.max.apply(null, lats),
      west: Math.min.apply(null, lons),
      east: Math.max.apply(null, lons),
    };

    // 標高データの範囲外なら、ここで止める（カート追加まで進ませない）
    if (!bboxInJapan(bbox)) {
      throw new Error(OUT_OF_RANGE_MESSAGE);
    }

    return {
      points: pts,
      segments: parsed.segments,
      segTrack: parsed.segTrack,
      trackNames: parsed.trackNames,
      trackName: parsed.trackName,
      pointCount: pts.length,
      distanceM: distance,
      elevationGainM: gain,
      elevationMinM: eleMin,
      elevationMaxM: eleMax,
      dateLabel: dateLabel,
      startLabel: startLabel,
      endLabel: endLabel,
      dateSegments: dateSegments,
      dateLabels: dateLabels,
      bbox: bbox,
    };
  }

  /*
    国土地理院の標高データが使える範囲。地形の起伏はこのDEMから起こしているので、
    ここを外れると模型が作れない。

    単純な外接矩形（緯度20〜46 / 経度123〜154）にすると、韓国・中国東北部・
    沿海州まで丸ごと入ってしまう。緯度帯ごとに矩形を分けて、そのぶんを削っている。
    ソウル・台北・ウラジオストクは除外できるが、**釜山あたりの韓国南東部だけは
    対馬と経度が重なるため矩形では切り分けられない**。ここは通ってしまう。
    厳密にやるなら国土地理院の標高APIへ問い合わせることになるが、
    外部APIへの依存とレイテンシが増えるので採っていない。

    判定を落とすのは「明らかに国外」を弾くため。境界付近の最終的な可否は
    プレビュー時の目視で担保する。
  */
  var JAPAN_REGIONS = [
    { name: '北海道', south: 41.2, north: 45.8, west: 139.3, east: 146.2 },
    { name: '本州〜九州', south: 30.0, north: 41.6, west: 128.3, east: 142.2 },
    { name: '南西諸島', south: 23.9, north: 30.2, west: 122.8, east: 131.5 },
    { name: '小笠原・南鳥島・沖ノ鳥島', south: 20.2, north: 27.8, west: 136.0, east: 154.2 },
  ];

  /*
    使う標高データが5mメッシュか10mメッシュかを、タイルの有無で判定する。

    5mメッシュ（dem5a）は整備範囲が限られる。全国を覆うのは10mメッシュ（dem_png）で、
    屋久島のように5mが無い山域が実際にある。**どちらを使うかは山域で決まり、
    購入者が選べるものではない**ので、注文時点で確かめて記録に残す。

    タイルの中身は要らないので HEAD で聞く（404が返れば未整備）。
    切り出し範囲の四隅と中心の5点を見る。1点だけだと、範囲の一部が
    未整備の山域を「5m」と記録してしまう。

    **5mが無いことは「10mを使う」を意味しない。** 圏外（国外）でも5mは404になる。
    そこで **5mが全点404のときだけ、10m（dem_png）の有無も確かめる**（2026-08-20）。
    10mも無ければ標高データが1枚も無い＝作れないので `'none'` を返す。
    これを入れるまで、韓国・釜山付近のGPX（矩形の国内判定を通り抜ける）が
    「全国を覆う10mメッシュを使います」と表示されたまま注文まで通っていた。

    **5mが1点でも200なら10mは聞かない。** その時点でデータがあることは確定していて、
    問い合わせが無駄に増えるだけ。
  */
  const DEM5A_TILE = 'https://cyberjapandata.gsi.go.jp/xyz/dem5a_png/{z}/{x}/{y}.png';
  const DEM10_TILE = 'https://cyberjapandata.gsi.go.jp/xyz/dem_png/{z}/{x}/{y}.png';
  const PROBE_Z = 14;

  function tileOf(lat, lon, z) {
    const n = Math.pow(2, z);
    const la = (lat * Math.PI) / 180;
    return {
      x: Math.floor(((lon + 180) / 360) * n),
      y: Math.floor(
        ((1 - Math.log(Math.tan(la) + 1 / Math.cos(la)) / Math.PI) / 2) * n
      ),
    };
  }

  async function probeMesh(crop) {
    const pts = [
      [crop.north, crop.west],
      [crop.north, crop.east],
      [crop.south, crop.west],
      [crop.south, crop.east],
      [(crop.north + crop.south) / 2, (crop.west + crop.east) / 2],
    ];
    const seen = {};
    const tiles = [];
    pts.forEach(function (p) {
      const t = tileOf(p[0], p[1], PROBE_Z);
      const key = t.x + '/' + t.y;
      if (seen[key]) return;
      seen[key] = true;
      tiles.push(t);
    });

    /* タイルの有無を聞く。**通信できなかった分は null にして判定から外す。**
       404（無い）と通信失敗（分からない）を混ぜると、回線が不安定なだけで
       注文を止めてしまう。 */
    function ask(tpl) {
      return Promise.all(
        tiles.map(function (t) {
          const u = tpl.replace('{z}', PROBE_Z).replace('{x}', t.x).replace('{y}', t.y);
          return fetch(u, { method: 'HEAD' })
            .then(function (r) {
              return r.status === 200;
            })
            .catch(function () {
              return null;
            });
        })
      ).then(function (res) {
        const known = res.filter(function (v) {
          return v !== null;
        });
        return { known: known.length, ok: known.filter(Boolean).length };
      });
    }

    try {
      const five = await ask(DEM5A_TILE);
      if (!five.known) return null;            // 全部失敗したら黙る
      if (five.ok === five.known) return '5m';
      if (five.ok > 0) return 'mixed';

      /* ここに来たのは「5mが全点404」。国内で5mが未整備なのか、
         そもそも圏外なのかは、10m（全国を覆う）を聞かないと分からない */
      const ten = await ask(DEM10_TILE);
      if (!ten.known) return null;
      return ten.ok > 0 ? '10m' : 'none';
    } catch (e) {
      return null;
    }
  }

  function isInJapan(lat, lon) {
    return JAPAN_REGIONS.some(function (r) {
      return lat >= r.south && lat <= r.north && lon >= r.west && lon <= r.east;
    });
  }

  /* 矩形の四隅がすべて範囲内かを見る。軌跡が一部でも外に出ていれば弾く。 */
  function bboxInJapan(bbox) {
    return (
      isInJapan(bbox.south, bbox.west) &&
      isInJapan(bbox.south, bbox.east) &&
      isInJapan(bbox.north, bbox.west) &&
      isInJapan(bbox.north, bbox.east)
    );
  }

  var OUT_OF_RANGE_MESSAGE =
    '日本国外の軌跡のようです。地形は国土地理院の標高データから起こしているため、いまのところ国内の山のみお受けしています。';

  var NO_DEM_MESSAGE =
    'この範囲には国土地理院の標高データがありません。地形を起こせないため、お作りできません。';

  var OUT_OF_RANGE_MESSAGE_POINT =
    '日本国外の地点のようです。地形は国土地理院の標高データから起こしているため、いまのところ国内のみお受けしています。';

  /* 短辺の下限。長辺に対する比率。
     縦走のような細長い軌跡だと短辺が長辺の2割ほどになり、細い板のような模型になる。
     置いたときの見え方と造形の安定性のため、この比率を下回らないよう範囲を広げる。 */
  var MIN_SHORT_RATIO = 2 / 3;

  /**
   * bbox を指定メートル分だけ四方に広げた切り出し範囲を返す。
   * ベース形状はスクエア（四角）で、縦横比は軌跡の範囲から決まるため
   * 正方形に整える処理はしない。ただし短辺が MIN_SHORT_RATIO を下回るときだけ、
   * 短い側に余白を足して比率を戻す（軌跡は中央に保たれる）。
   */
  function cropArea(bbox, marginM) {
    const midLat = (bbox.south + bbox.north) / 2;
    const mPerDegLat = 111132.92 - 559.82 * Math.cos((2 * midLat * Math.PI) / 180);
    const mPerDegLon = 111412.84 * Math.cos((midLat * Math.PI) / 180);

    const dLat = marginM / mPerDegLat;
    const dLon = marginM / mPerDegLon;

    const box = {
      south: bbox.south - dLat,
      north: bbox.north + dLat,
      west: bbox.west - dLon,
      east: bbox.east + dLon,
    };

    box.widthM = (box.east - box.west) * mPerDegLon;
    box.heightM = (box.north - box.south) * mPerDegLat;

    // 短辺が下限を割っていたら、短い側だけ左右対称に広げる
    const minShortM = Math.max(box.widthM, box.heightM) * MIN_SHORT_RATIO;
    box.widened = false;
    if (box.widthM < minShortM) {
      const addM = (minShortM - box.widthM) / 2;
      box.west -= addM / mPerDegLon;
      box.east += addM / mPerDegLon;
      box.widthM = minShortM;
      box.widened = true;
    } else if (box.heightM < minShortM) {
      const addM = (minShortM - box.heightM) / 2;
      box.south -= addM / mPerDegLat;
      box.north += addM / mPerDegLat;
      box.heightM = minShortM;
      box.widened = true;
    }

    return box;
  }

  /** 長辺の指定mmから、短辺のmmを縦横比で決める（縦は自動） */
  function modelDimensions(crop, longEdgeMm) {
    const longM = Math.max(crop.widthM, crop.heightM);
    const shortM = Math.min(crop.widthM, crop.heightM);
    const shortMm = (shortM / longM) * longEdgeMm;
    const landscape = crop.widthM >= crop.heightM;
    return {
      widthMm: landscape ? longEdgeMm : shortMm,
      depthMm: landscape ? shortMm : longEdgeMm,
      longEdgeMm: longEdgeMm,
      shortEdgeMm: shortMm,
    };
  }

  window.R3DGpx = {
    MIN_SHORT_RATIO: MIN_SHORT_RATIO,
    parse: function (xmlText) {
      return summarize(parsePoints(xmlText));
    },
    cropArea: cropArea,
    probeMesh: probeMesh,
    modelDimensions: modelDimensions,
    isInJapan: isInJapan,
    bboxInJapan: bboxInJapan,
    OUT_OF_RANGE_MESSAGE: OUT_OF_RANGE_MESSAGE,
    OUT_OF_RANGE_MESSAGE_POINT: OUT_OF_RANGE_MESSAGE_POINT,
    NO_DEM_MESSAGE: NO_DEM_MESSAGE,
    ELE_MIN: ELE_MIN,
    ELE_MAX: ELE_MAX,
    toJstDateString: toJstDateString,
    toJstDateTimeString: toJstDateTimeString,
  };
})();
